import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  CheckCircle,
  CameraAlt,
  Security,
  HealthAndSafety,
  Science,
  PhotoLibrary,
  Mic,
  Edit,
} from '@mui/icons-material'

const Landing: React.FC = () => {
  const navigate = useNavigate()

  const features = [
    {
      icon: <CameraAlt fontSize="large" />,
      title: 'AI-Powered Data Entry',
      description: 'Take photos of medical documents, use voice input, or type your health information',
      link: '/features/ai-data-entry',
    },
    {
      icon: <HealthAndSafety fontSize="large" />,
      title: 'Comprehensive Health Profile',
      description: 'Track diseases, medications, symptoms, test results, and procedures in one place',
      link: '/features/health-profile',
    },
    {
      icon: <Science fontSize="large" />,
      title: 'Clinical Trial Search',
      description: 'Access ClinicalTrials.gov database and find trials matched to your condition',
      link: '/features/trial-search',
    },
    {
      icon: <Security fontSize="large" />,
      title: 'Privacy & Security',
      description: 'Your health data is secure and protected with HIPAA-compliant practices',
      link: '/features/privacy-security',
    },
  ]

  const inputMethods = [
    { icon: <CameraAlt />, text: 'Camera: Snap photos of medical documents' },
    { icon: <PhotoLibrary />, text: 'Photo Library: Upload existing images' },
    { icon: <Mic />, text: 'Voice Input: Speak your medical history' },
    { icon: <Edit />, text: 'Text Entry: Type your health information' },
  ]

  const benefits = [
    'Track diseases and conditions',
    'Manage medications and dosages',
    'Record symptoms and their timeline',
    'Store test results and procedures',
    'Document surgeries and medical history',
    'BMI calculator with unit conversion',
  ]

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <img 
                src="/logo.png" 
                alt="BlueBit Logo" 
                style={{ height: '40px', width: 'auto' }} 
              />
              <Typography variant="h5" fontWeight="bold">
                <Box component="span" sx={{ color: '#1976d2' }}>BlueBit</Box>
                {' '}
                <Box component="span" sx={{ color: 'primary.main' }}>TrialFinder</Box>
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button variant="contained" onClick={() => navigate('/register')}>
                Get Started
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 10,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h2" fontWeight="bold" gutterBottom>
                Find Clinical Trials That Match Your Health Condition
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                AI-powered clinical trial finder. Upload medical records via photo, voice, or text. 
                Get matched to relevant research studies instantly.
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Button 
                  variant="contained" 
                  size="large" 
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                  onClick={() => navigate('/register')}
                >
                  Start Free Trial
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </Button>
              </Stack>
              <Stack direction="row" spacing={2} mt={3} flexWrap="wrap">
                <Chip 
                  icon={<CheckCircle />} 
                  label="HIPAA Compliant" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
                <Chip 
                  icon={<CheckCircle />} 
                  label="Privacy-Focused" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
                <Chip 
                  icon={<CheckCircle />} 
                  label="Easy to Use" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box 
                sx={{ 
                  bgcolor: 'white', 
                  borderRadius: 4, 
                  p: 3,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}
              >
                <Typography variant="h6" color="text.primary" gutterBottom fontWeight="bold">
                  Get Started in 3 Easy Steps
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                        1
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary="Share Your Health Information"
                      secondary="Take photos, use voice, or type your medical data"
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                        2
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary="AI Analyzes Your Data"
                      secondary="Advanced AI organizes your health profile"
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                        3
                      </Box>
                    </ListItemIcon>
                    <ListItemText 
                      primary="Get Matched to Trials"
                      secondary="Discover relevant clinical trials near you"
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" fontWeight="bold" gutterBottom>
          Powerful Features
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Everything you need to manage your health data and find clinical trials
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  textAlign: 'center', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    boxShadow: 6,
                    transform: 'translateY(-8px)',
                  } 
                }}
                onClick={() => navigate(feature.link)}
              >
                <CardContent>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {feature.description}
                  </Typography>
                  <Button size="small" variant="text" color="primary">
                    Learn More →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Input Methods Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Multiple Ways to Enter Your Health Data
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Choose the method that works best for you. Our AI extracts and organizes your health information automatically.
              </Typography>
              <List>
                {inputMethods.map((method, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ color: 'primary.main' }}>
                      {method.icon}
                    </ListItemIcon>
                    <ListItemText primary={method.text} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Comprehensive Health Profile
                  </Typography>
                  <List>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText primary={benefit} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Ready to Find Clinical Trials?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Join patients who are discovering clinical trials that could benefit their health journey
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
            onClick={() => navigate('/register')}
          >
            Get Started Free
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No credit card required • Privacy-focused • Secure
          </Typography>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                <Box component="span" sx={{ color: '#3498db' }}>BlueBit</Box> TrialFinder
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                AI-powered clinical trial finder. Find trials that match your specific health condition.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Legal
              </Typography>
              <Stack spacing={1}>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', opacity: 0.8 }}
                  onClick={() => navigate('/privacy')}
                >
                  Privacy Policy
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', opacity: 0.8 }}
                  onClick={() => navigate('/terms')}
                >
                  Terms of Service
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Support
              </Typography>
              <Stack spacing={1}>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', opacity: 0.8 }}
                  onClick={() => navigate('/about')}
                >
                  About Us
                </Button>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Email: info@bluebithealth.com
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: 1, borderColor: 'grey.800', mt: 4, pt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © 2025 BlueBit Health LLC. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 1 }}>
              This app is NOT a substitute for professional medical advice, diagnosis, or treatment.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default Landing

